import { Button, FormControl } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

import Page from "~/components/Page";
import TableControls from "~/components/TableControls";
import Filter from "~/components/Filter";
import AdminServicesTable from "~/tables/AdminServicesTable";
import RemoveServiceModal from "~/modals/RemoveServiceModal";
import CreateServiceModal from "~/modals/CreateServiceModal";
import { removeFromArray } from '~/utils/utils';
import { useTrans } from '~/hooks/useTrans';
import { apiGetServices } from "~/providers/api";
import { IService } from "~/providers/interfaces";
import ErrorInfo from '~/components/ErrorInfo';
import { myUseState } from "~/hooks/myUseState";
import { useLang } from "~/hooks/useLang";
import AdminEditServiceDialog from "~/modals/AdminEditServiceDialog";

import "./style.less";

type TFilter = "backend" | "none";

interface IState {
	loading: boolean;
	isDirty: boolean;
	services: Array<IService>;
	servicesOrig: Array<IService>;
	createService: boolean;
	editService: IService;
	removeService: IService;
	errorMsg: string;
}

const INIT_STATE: IState = {
	loading: true,
	isDirty: false,
	services: [],
	servicesOrig: [],
	createService: false,
	editService: null,
	removeService: null,
	errorMsg: "",
};

export default function AdminServicesPage() {
	const { trans } = useTrans();
	const { lang } = useLang();
	const { state, setState, updateState } = myUseState<IState>(INIT_STATE);

	function onRemove() {
		setState(prev => ({
			...prev,
			services: removeFromArray(prev.services, service => service.id === prev.removeService.id),
			removeService: null,
		}));
	}

	function onEdit(service: IService) {
		setState(prev => {
			const services = prev.services.slice();
			const filtered = services.filter(item => item.id === service.id);

			if (filtered.length) {
				Object.assign(filtered[0], service);
			}

			return {
				...prev,
				services,
				editService: null,
			};
		});
	}

	function onCreate(service: IService) {
		setState(prev => ({
			...prev,
			services: [
				...prev.services,
				service,
			],
			servicesOrig: [
				...prev.servicesOrig,
				service,
			],
			createService: false,
			isDirty: false,
		}));
	}

	function onFilter(filterType: TFilter, filterValue: string) {
		setState(prev => {
			const filtered = prev.servicesOrig.filter(item => {
				switch (filterType) {
					case "backend":
						return item.service.indexOf(filterValue) !== -1;

					default:
				}

				return true;
			});

			return {
				...prev,
				services: filtered,
			};
		});
	}

	async function loadData() {
		updateState(INIT_STATE);

		const servicesData = await apiGetServices(lang);

		updateState({
			loading: false,
			...servicesData.noErrors
				? {
					services: servicesData.data.slice(),
					servicesOrig: servicesData.data,
				}
				: {},
			errorMsg: servicesData.errors.join(", "),
		});
	}

	function onPageLoad(isAuthorized: boolean) {
		if (isAuthorized) {
			loadData();
		}
	}

	return <Page title={trans("menu.adminServices")} breadcrumbs={[trans("administration"), trans("menu.adminServices")]} checkInternalRole="internal_admin" onLoad={onPageLoad} isDirty={state.isDirty} seoTitleTrans="seo.title.admin.services">
		<TableControls className="adminServicesPage__tableControls">
			<div className="adminServicesPage__filterCont">
				<Filter<TFilter> type="backend" disabledTypes={["none"]} onFilter={onFilter}
					selectTitle={trans("adminServicesPage.filter.title")} inputTitle={trans("adminServicesPage.filter.filterValue")}
					selectItems={[
						{ value: "backend", text: trans("adminServicesPage.filter.types.backend") },
						{ value: "none", text: trans("adminServicesPage.filter.types.none") },
					]} />
			</div>
			<FormControl className="adminServicesPage__addForm" size="small">
				<Button color="success" variant="contained" onClick={() => updateState({ createService: true })} className="adminServicesPage__addBtn btn-with-icon">
					<AddIcon /> { trans("adminServicesPage.addService") }
				</Button>
			</FormControl>
		</TableControls>
		<AdminServicesTable loading={state.loading} items={state.services} onEdit={service => updateState({ editService: service })} onRemove={service => updateState({ removeService: service })} />
		{ state.errorMsg && <ErrorInfo msg={state.errorMsg} className="margin-top-large" /> }
		{ state.editService && <AdminEditServiceDialog service={state.editService} onClose={() => updateState({ editService: null })} onSave={onEdit} /> }
		{ state.removeService && <RemoveServiceModal serviceId={state.removeService.id} onClose={() => updateState({ removeService: null })} onSave={onRemove} /> }
		{ state.createService && <CreateServiceModal rootServices={state.services.filter(service => service.rootServiceId === null)} onClose={() => updateState({ createService: false, isDirty: false })} onSave={onCreate}
			onChange={isDirty => updateState({ isDirty })} /> }
	</Page>;
}
